import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Instructor {
  id: string;
  instructor_user_id?: string;
  manual_name?: string;
  name?: string;
  username?: string;
  avatar_url?: string;
  belt_rank?: string;
  is_manual: boolean;
  created_at: string;
}

interface UseProfileInstructorsReturn {
  instructors: Instructor[];
  loading: boolean;
  error: string | null;
  addInstructor: (instructor_user_id?: string, manual_name?: string) => Promise<void>;
  removeInstructor: (instructor_user_id?: string, manual_name?: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useProfileInstructors(profileId: string | undefined): UseProfileInstructorsReturn {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstructors = useCallback(async () => {
    if (!profileId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_profile_instructors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          profile_id: profileId,
          all: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch instructors');
      }

      const result = await response.json();
  
      setInstructors(result.instructors || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch instructors');
      console.error('Error fetching instructors:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  const addInstructor = useCallback(async (instructor_user_id?: string, manual_name?: string) => {
    if (!profileId) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add_profile_instructor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          profile_id: profileId,
          instructor_user_id,
          manual_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add instructor');
      }

      // Refetch instructors after adding
      await fetchInstructors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add instructor');
      console.error('Error adding instructor:', err);
    }
  }, [profileId, fetchInstructors]);

  const removeInstructor = useCallback(async (instructor_user_id?: string, manual_name?: string) => {
    if (!profileId) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/remove_profile_instructor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          profile_id: profileId,
          instructor_user_id,
          manual_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to remove instructor');
      }

      // Refetch instructors after removing
      await fetchInstructors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove instructor');
      console.error('Error removing instructor:', err);
    }
  }, [profileId, fetchInstructors]);

  useEffect(() => {
    fetchInstructors();
  }, [fetchInstructors]);

  return {
    instructors,
    loading,
    error,
    addInstructor,
    removeInstructor,
    refetch: fetchInstructors
  };
}; 